import { useCallback, useEffect, useState, useContext } from "react";
import { createPortal } from "react-dom";
import ProfileNavigation from "../../../components/common/ProfileNavigation";
import useUser, { deleteAuthorized, getAuthorized, isUserDoctor, postAuthorized, patchAuthorized } from "../../../hooks/User";
import { useLocation, useNavigate } from "react-router-dom";
import customParseFormat from 'dayjs/plugin/customParseFormat';
import dayjs from "dayjs";
import { EvaluateDays, EvaluateEvery, TimezoneMap } from "../Scheduler/Scheduler";
import { ErrorHandlerContext, ErrorHandlerContextProvider } from "../../../contexts/ErrorHandlerContext";
import ErrorHandler from "../../../components/common/ErrorHandler";
import { getPatientDisplayName, getPatientInitials, hasPatientName } from "../../../utils/patientUtils";
import { ROUTES } from "../../../routes";
import {
    StethoscopeIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    FileTextIcon as ImportedFileTextIcon,
    RepeatIcon as ImportedRepeatIcon,
    PlusIcon as ImportedPlusIcon,
    CheckIcon as ImportedCheckIcon,
    XIcon as ImportedXIcon,
    EditIcon
} from "../../../components/icons";
import SEO from "../../../components/SEO";
import { MiniTutorial, PATIENT_SCHEDULED_STEPS } from "../../../components/MiniTutorial";

import "./patientscheduledsurveys.css";

dayjs.extend(customParseFormat);

// Icons
const CalendarIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
        <line x1="16" y1="2" x2="16" y2="6"/>
        <line x1="8" y1="2" x2="8" y2="6"/>
        <line x1="3" y1="10" x2="21" y2="10"/>
    </svg>
);

const ClockIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/>
        <polyline points="12 6 12 12 16 14"/>
    </svg>
);

const RepeatIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="m17 2 4 4-4 4"/>
        <path d="M3 11v-1a4 4 0 0 1 4-4h14"/>
        <path d="m7 22-4-4 4-4"/>
        <path d="M21 13v1a4 4 0 0 1-4 4H3"/>
    </svg>
);

const MapPinIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/>
        <circle cx="12" cy="10" r="3"/>
    </svg>
);

const TrashIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 6h18"/>
        <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/>
        <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/>
        <line x1="10" x2="10" y1="11" y2="17"/>
        <line x1="14" x2="14" y1="11" y2="17"/>
    </svg>
);

const FileTextIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/>
        <polyline points="14 2 14 8 20 8"/>
        <line x1="16" y1="13" x2="8" y2="13"/>
        <line x1="16" y1="17" x2="8" y2="17"/>
        <line x1="10" y1="9" x2="8" y2="9"/>
    </svg>
);

const PlusIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="5" x2="12" y2="19"/>
        <line x1="5" y1="12" x2="19" y2="12"/>
    </svg>
);

const XIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 6 6 18"/>
        <path d="m6 6 12 12"/>
    </svg>
);

const AlertTriangleIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/>
        <line x1="12" y1="9" x2="12" y2="13"/>
        <line x1="12" y1="17" x2="12.01" y2="17"/>
    </svg>
);

// Constants
const CastFreqTypeToStr = {
    once_per_day: "1 раз в день",
    multiple_times_per_day: "Несколько раз в день",
    every_few_days: "Каждые несколько дней",
    weekly: "Еженедельно"
};

const WeekArray = ["Понедельник", "Вторник", "Среда", "Четверг", "Пятница", "Суббота", "Воскресенье"];

// Format helpers
function formatTime(timeString) {
    return dayjs(timeString, "HH:mm:ss").format("HH:mm");
}

function formatDate(dateString) {
    return dayjs(dateString, "YYYY-MM-DD").format("DD.MM.YYYY");
}

// Patient Header Component
function PatientHeader({ patientId, patientName }) {
    // Создаем объект пациента для использования утилиты
    const patient = patientName ? { full_name: patientName } : null;
    const displayName = getPatientDisplayName(patient || { id: patientId });
    const initials = getPatientInitials(patient || { id: patientId });
    const hasName = hasPatientName(patient);
    
    return (
        <div className="scheduled-header">
            <div className="patient-header-info">
                <div className="patient-avatar-large">
                    {initials}
                </div>
                <div className="patient-header-details">
                    <h1>Запланированные опросы</h1>
                    <p className={`patient-name ${!hasName ? 'no-name' : ''}`}>{displayName}</p>
                </div>
            </div>
        </div>
    );
}

// Schedule Card Component
function ScheduleCard({ schedule, onEdit, onDelete, onViewDetails }) {
    const isActive = schedule.is_active !== false;
    const nextDate = schedule.next_scheduled_date;
    const isOverdue = nextDate && dayjs(nextDate).isBefore(dayjs(), 'day');
    
    return (
        <div className={`schedule-card ${!isActive ? 'inactive' : ''}`}>
            <div className="schedule-card-header">
                <div className="schedule-title-section">
                    <h3 className="schedule-title">{schedule.title}</h3>
                    <span className={`schedule-status ${isActive ? 'active' : 'inactive'}`}>
                        {isActive ? 'Активно' : 'Неактивно'}
                    </span>
                </div>
                <div className="schedule-card-actions">
                    <button
                        className="btn-icon btn-edit"
                        onClick={() => onEdit(schedule)}
                        title="Редактировать назначение"
                    >
                        <EditIcon size={18} />
                    </button>
                    <button
                        className="btn-icon btn-delete"
                        onClick={() => onDelete(schedule)}
                        title="Удалить назначение"
                    >
                        <TrashIcon />
                    </button>
                </div>
            </div>
            
            <div className="schedule-survey-name">
                <FileTextIcon />
                <span>Опрос: {schedule.survey_title || `ID: ${schedule.survey_id}`}</span>
            </div>
            
            <div className="schedule-info-grid">
                <div className="info-item">
                    <RepeatIcon />
                    <div>
                        <span className="info-label">Тип</span>
                        <span className="info-value">{CastFreqTypeToStr[schedule.frequency_type]}</span>
                    </div>
                </div>
                
                <div className="info-item">
                    <ClockIcon />
                    <div>
                        <span className="info-label">Время</span>
                        <span className="info-value">
                            {schedule.scheduled_times?.map(formatTime).join(", ")}
                        </span>
                    </div>
                </div>
                
                <div className="info-item">
                    <MapPinIcon />
                    <div>
                        <span className="info-label">Часовой пояс</span>
                        <span className="info-value">{TimezoneMap[schedule.timezone] || schedule.timezone}</span>
                    </div>
                </div>
                
                <div className="info-item">
                    <CalendarIcon />
                    <div>
                        <span className="info-label">Следующее</span>
                        <span className={`info-value ${isOverdue ? 'overdue' : ''}`}>
                            {nextDate ? formatDate(nextDate) : '—'}
                        </span>
                    </div>
                </div>
            </div>
            
            <div className="schedule-card-footer">
                <span className="schedule-period">
                    С {formatDate(schedule.start_date)}
                    {schedule.end_date ? ` по ${formatDate(schedule.end_date)}` : ' (без окончания)'}
                </span>
                <button 
                    className="btn-details"
                    onClick={() => onViewDetails(schedule)}
                >
                    Подробнее
                </button>
            </div>
        </div>
    );
}

// Details Modal Component
function DetailsModal({ schedule, onClose }) {
    if (!schedule) return null;
    
    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>{schedule.title}</h2>
                    <button className="modal-close" onClick={onClose}>
                        <XIcon />
                    </button>
                </div>
                
                <div className="modal-body">
                    <div className="detail-section">
                        <h4>Основная информация</h4>
                        <div className="detail-row">
                            <span className="detail-label">Опрос</span>
                            <span className="detail-value">{schedule.survey_title || `ID: ${schedule.survey_id}`}</span>
                        </div>
                        <div className="detail-row">
                            <span className="detail-label">Тип расписания</span>
                            <span className="detail-value">{CastFreqTypeToStr[schedule.frequency_type]}</span>
                        </div>
                        <div className="detail-row">
                            <span className="detail-label">Статус</span>
                            <span className={`detail-value status-${schedule.is_active !== false ? 'active' : 'inactive'}`}>
                                {schedule.is_active !== false ? 'Активно' : 'Неактивно'}
                            </span>
                        </div>
                    </div>
                    
                    <div className="detail-section">
                        <h4>Расписание</h4>
                        <div className="detail-row">
                            <span className="detail-label">Время</span>
                            <span className="detail-value">
                                {schedule.scheduled_times?.map(t => formatTime(t)).join(", ")}
                            </span>
                        </div>
                        <div className="detail-row">
                            <span className="detail-label">Часовой пояс</span>
                            <span className="detail-value">{TimezoneMap[schedule.timezone] || schedule.timezone}</span>
                        </div>
                        {schedule.frequency_type === 'weekly' && schedule.days_of_week && (
                            <div className="detail-row">
                                <span className="detail-label">Дни недели</span>
                                <span className="detail-value">
                                    {schedule.days_of_week.sort((a, b) => a - b).map(d => WeekArray[d]).join(", ")}
                                </span>
                            </div>
                        )}
                        {schedule.frequency_type === 'every_few_days' && schedule.interval_days && (
                            <div className="detail-row">
                                <span className="detail-label">Интервал</span>
                                <span className="detail-value">
                                    {EvaluateEvery(schedule.interval_days)} {schedule.interval_days} {EvaluateDays(schedule.interval_days)}
                                </span>
                            </div>
                        )}
                    </div>
                    
                    <div className="detail-section">
                        <h4>Период действия</h4>
                        <div className="detail-row">
                            <span className="detail-label">Начало</span>
                            <span className="detail-value">{formatDate(schedule.start_date)}</span>
                        </div>
                        {schedule.end_date && (
                            <div className="detail-row">
                                <span className="detail-label">Окончание</span>
                                <span className="detail-value">{formatDate(schedule.end_date)}</span>
                            </div>
                        )}
                        <div className="detail-row">
                            <span className="detail-label">Следующее прохождение</span>
                            <span className="detail-value">{schedule.next_scheduled_date ? formatDate(schedule.next_scheduled_date) : '—'}</span>
                        </div>
                    </div>
                    
                    <div className="detail-section">
                        <h4>Напоминания</h4>
                        <div className="detail-row">
                            <span className="detail-label">Максимум напоминаний</span>
                            <span className="detail-value">{schedule.max_reminders || 3}</span>
                        </div>
                        <div className="detail-row">
                            <span className="detail-label">Интервал напоминаний</span>
                            <span className="detail-value">{schedule.reminder_interval_minutes || 60} минут</span>
                        </div>
                    </div>
                </div>
                
                <div className="modal-footer">
                    <button className="btn-secondary" onClick={onClose}>Закрыть</button>
                </div>
            </div>
        </div>
    );
}

// Delete Confirmation Modal
function DeleteConfirmModal({ schedule, onConfirm, onCancel }) {
    if (!schedule) return null;
    
    return (
        <div className="modal-overlay" onClick={onCancel}>
            <div className="modal-content modal-confirm" onClick={(e) => e.stopPropagation()}>
                <div className="confirm-icon">
                    <AlertTriangleIcon />
                </div>
                <h3>Удалить назначение?</h3>
                <p>Вы уверены, что хотите удалить назначение <strong>"{schedule.title}"</strong>?</p>
                <p className="confirm-hint">Это действие нельзя отменить.</p>
                
                <div className="confirm-actions">
                    <button className="btn-secondary" onClick={onCancel}>Отмена</button>
                    <button className="btn-danger" onClick={onConfirm}>Удалить</button>
                </div>
            </div>
        </div>
    );
}

// Main Component
function PatientScheduled() {
    const { data, error, loading: userLoading } = useUser();
    const navigate = useNavigate();
    const location = useLocation();
    const { setErrorList } = useContext(ErrorHandlerContext);
    
    const [patientId, setPatientId] = useState(null);
    const [patientName, setPatientName] = useState("");
    const [scheduled, setScheduled] = useState([]);
    const [initialLoading, setInitialLoading] = useState(true);
    const [selectedSchedule, setSelectedSchedule] = useState(null);
    const [scheduleToDelete, setScheduleToDelete] = useState(null);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [editingSchedule, setEditingSchedule] = useState(null);
    
    // Auth and routing check
    useEffect(() => {
        if (userLoading) return;
        if (error) {
            navigate(ROUTES.AUTH, { viewTransition: true });
            return;
        }
        if (!isUserDoctor(data)) {
            navigate(ROUTES.PROFILE, { viewTransition: true });
            return;
        }
        
        const id = location?.state?.patientId;
        if (id === null || id === undefined) {
            navigate(ROUTES.PATIENTS, { viewTransition: true });
            return;
        }
        
        setPatientId(id);
        setPatientName(location?.state?.patientName || "");
    }, [data, error, userLoading, navigate, location]);
    
    // Load patient name if not provided
    useEffect(() => {
        if (!patientId || patientName) return;
        
        getAuthorized("/doctor/my_patients", {})
            .then(response => {
                const patients = response.data?.patients || [];
                const patient = patients.find(p => p.id === patientId);
                if (patient) {
                    setPatientName(getPatientDisplayName(patient));
                }
            })
            .catch(err => console.error("Failed to load patient info:", err));
    }, [patientId, patientName]);
    
    // Load scheduled surveys
    const loadScheduled = useCallback(async (opts = {}) => {
        if (!patientId) return;

        const isInitial = opts.initial ?? false;
        if (isInitial) setInitialLoading(true);
        try {
            const response = await getAuthorized("/doctor/patient_scheduled_surveys", {
                params: { patient_id: patientId }
            });
            setScheduled(response.data || []);
        } catch (err) {
            console.error("Failed to load scheduled surveys:", err);
            setErrorList(prev => [...prev, "Ошибка при загрузке запланированных опросов"]);
        } finally {
            if (isInitial) setInitialLoading(false);
        }
    }, [patientId, setErrorList]);
    
    useEffect(() => {
        loadScheduled({ initial: true });
    }, [loadScheduled]);
    
    // Delete handlers
    const handleDeleteClick = useCallback((schedule) => {
        setScheduleToDelete(schedule);
    }, []);
    
    const handleDeleteConfirm = useCallback(async () => {
        if (!scheduleToDelete) return;

        try {
            await deleteAuthorized("/doctor/delete_scheduled_survey_by_id/" + scheduleToDelete.id);
            setScheduleToDelete(null);
            await loadScheduled();
        } catch (err) {
            console.error("Failed to delete scheduled survey:", err);
            setErrorList(prev => [...prev, err.response?.data?.error_description || "Ошибка при удалении назначения"]);
        }
    }, [scheduleToDelete, loadScheduled, setErrorList]);
    
    const handleDeleteCancel = useCallback(() => {
        setScheduleToDelete(null);
    }, []);

    const handleCreateSchedule = useCallback(() => {
        setEditingSchedule(null);
        setIsDrawerOpen(true);
    }, []);

    const handleEditSchedule = useCallback((schedule) => {
        setEditingSchedule(schedule);
        setIsDrawerOpen(true);
    }, []);

    const handleCloseDrawer = useCallback(() => {
        setIsDrawerOpen(false);
        setEditingSchedule(null);
    }, []);

    const handleSaveSchedule = useCallback(async (payload, scheduleId) => {
        if (scheduleId) {
            await patchAuthorized("/doctor/update_scheduled_survey/" + scheduleId, payload);
        } else {
            await postAuthorized("/doctor/schedule_survey", payload);
        }
        await loadScheduled({ silent: true });
    }, [loadScheduled]);
    
    // Loading state
    if (userLoading || initialLoading) {
        return (
            <div className="scheduled-page">
                <ProfileNavigation isDoctor={true} />
                <div className="scheduled-loading">
                    <div className="spinner"></div>
                    <p>Загрузка расписания...</p>
                </div>
            </div>
        );
    }
    
    const activeCount = scheduled.filter(s => s.is_active !== false).length;
    
    return (
        <div className="scheduled-page">
            <ProfileNavigation isDoctor={isUserDoctor(data)} />
            
            <div className="scheduled-container">
                <PatientHeader patientId={patientId} patientName={patientName} />
                
                <div className="scheduled-toolbar">
                    <div className="scheduled-stats">
                        <span>Всего назначений: {activeCount}</span>
                        <span className="stats-active">Показаны только активные</span>
                    </div>
                    <button 
                        className="btn-primary btn-create" 
                        onClick={handleCreateSchedule}
                    >
                        <PlusIcon />
                        <span>Назначить опрос</span>
                    </button>
                </div>
                
                {activeCount === 0 ? (
                    <div className="empty-scheduled-state">
                        <div className="empty-icon">
                            <CalendarIcon />
                        </div>
                        <h3>Нет запланированных опросов</h3>
                        <p>У этого пациента пока нет назначенных опросов</p>
                        <button 
                            className="btn-primary"
                            onClick={handleCreateSchedule}
                        >
                            Создать назначение
                        </button>
                    </div>
                ) : (
                    <>
                        <div className="scheduled-grid">
                            {scheduled.filter(s => s.is_active !== false).map((schedule) => (
                                <ScheduleCard
                                    key={schedule.id}
                                    schedule={schedule}
                                    onEdit={handleEditSchedule}
                                    onDelete={handleDeleteClick}
                                    onViewDetails={setSelectedSchedule}
                                />
                            ))}
                        </div>
                    </>
                )}
            </div>
            
            {/* Modals */}
            <DetailsModal 
                schedule={selectedSchedule} 
                onClose={() => setSelectedSchedule(null)} 
            />
            
            <DeleteConfirmModal
                schedule={scheduleToDelete}
                onConfirm={handleDeleteConfirm}
                onCancel={handleDeleteCancel}
            />

            <SurveyAssignmentDrawer
                isOpen={isDrawerOpen}
                onClose={handleCloseDrawer}
                onSave={handleSaveSchedule}
                patient={{ id: patientId, full_name: patientName }}
                initialData={editingSchedule}
            />
            
            <MiniTutorial steps={PATIENT_SCHEDULED_STEPS} pageKey="patient-scheduled" />
        </div>
    );
}

// Survey Assignment Drawer Component (lightweight, tailored for scheduled page)
function SurveyAssignmentDrawer({ isOpen, onClose, onSave, patient, initialData }) {
    const navigate = useNavigate();
    const [surveys, setSurveys] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [activeSection, setActiveSection] = useState("select");
    const [errors, setErrors] = useState({});
    
    const isEditing = !!initialData?.id;

    const [formData, setFormData] = useState({
        survey_id: null,
        title: "",
        description: "",
        frequency_type: "once_per_day",
        scheduled_times: ["09:00"],
        start_date: dayjs().format("YYYY-MM-DD"),
        end_date: "",
        timezone: "Europe/Moscow",
        max_reminders: 3,
        reminder_interval_minutes: 60,
        days_of_week: [],
        interval_days: 2,
        is_active: true
    });

    const frequencyLabels = {
        once_per_day: "1 раз в день",
        multiple_times_per_day: "Несколько раз в день",
        every_few_days: "Раз в несколько дней",
        weekly: "По дням недели"
    };

    const weekDays = [
        { value: 0, label: "Пн" },
        { value: 1, label: "Вт" },
        { value: 2, label: "Ср" },
        { value: 3, label: "Чт" },
        { value: 4, label: "Пт" },
        { value: 5, label: "Сб" },
        { value: 6, label: "Вс" }
    ];

    const timezones = [
        { value: "Europe/Moscow", label: "Москва (UTC+3)" },
        { value: "Europe/Samara", label: "Самара (UTC+4)" },
        { value: "Asia/Yekaterinburg", label: "Екатеринбург (UTC+5)" },
        { value: "Asia/Novosibirsk", label: "Новосибирск (UTC+7)" },
        { value: "Asia/Vladivostok", label: "Владивосток (UTC+10)" }
    ];

    useEffect(() => {
        if (!isOpen) return;
        setLoading(true);
        getAuthorized("/doctor/my_surveys")
            .then(response => {
                // Фильтруем опросы - показываем только не черновики
                const allSurveys = response.data?.surveys || [];
                const activeSurveys = allSurveys.filter(s => s.status !== 'draft');
                setSurveys(activeSurveys);
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;
        if (initialData) {
            setFormData({
                survey_id: initialData.survey_id,
                title: initialData.title || "",
                description: initialData.description || "",
                frequency_type: initialData.frequency_type || "once_per_day",
                scheduled_times: initialData.scheduled_times?.map(t => formatTime(t)) || ["09:00"],
                start_date: initialData.start_date || dayjs().format("YYYY-MM-DD"),
                end_date: initialData.end_date || "",
                timezone: initialData.timezone || "Europe/Moscow",
                max_reminders: initialData.max_reminders ?? 3,
                reminder_interval_minutes: initialData.reminder_interval_minutes ?? 60,
                days_of_week: initialData.days_of_week || [],
                interval_days: initialData.interval_days || 2,
                is_active: initialData.is_active !== false
            });
            setErrors({});
            setActiveSection("settings");
        } else {
            setFormData({
                survey_id: null,
                title: "",
                description: "",
                frequency_type: "once_per_day",
                scheduled_times: ["09:00"],
                start_date: dayjs().format("YYYY-MM-DD"),
                end_date: "",
                timezone: "Europe/Moscow",
                max_reminders: 3,
                reminder_interval_minutes: 60,
                days_of_week: [],
                interval_days: 2,
                is_active: true
            });
            setErrors({});
            setActiveSection("select");
        }
    }, [isOpen, initialData]);

    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape' && isOpen) onClose();
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, onClose]);

    useEffect(() => {
        document.body.style.overflow = isOpen ? 'hidden' : '';
        return () => { document.body.style.overflow = ''; };
    }, [isOpen]);

    const validate = () => {
        const newErrors = {};
        if (!isEditing && !formData.survey_id) {
            newErrors.survey = "Выберите опрос";
            setActiveSection("select");
        }
        if (!formData.title.trim()) {
            newErrors.title = "Введите название назначения";
            setActiveSection("settings");
        }
        if (formData.frequency_type === "weekly" && !formData.days_of_week.length) {
            newErrors.days = "Выберите хотя бы один день";
            setActiveSection("schedule");
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;
        setIsSubmitting(true);
        try {
            const payload = {
                title: formData.title,
                description: formData.description || undefined,
                frequency_type: formData.frequency_type,
                scheduled_times: formData.scheduled_times.map(t => t + ":00"),
                start_date: formData.start_date,
                end_date: formData.end_date || undefined,
                timezone: formData.timezone,
                max_reminders: formData.max_reminders,
                reminder_interval_minutes: formData.reminder_interval_minutes
            };

            if (isEditing) {
                payload.is_active = formData.is_active;
            } else {
                payload.patient_id = patient?.id;
                payload.survey_id = formData.survey_id;
            }

            if (formData.frequency_type === "weekly") {
                payload.days_of_week = formData.days_of_week;
            }
            if (formData.frequency_type === "every_few_days") {
                payload.interval_days = formData.interval_days;
            }
            if (formData.frequency_type === "multiple_times_per_day") {
                payload.times_per_day = formData.scheduled_times.length;
            }

            await onSave(payload, isEditing ? initialData.id : null);
            onClose();
        } catch (error) {
            console.error("Failed to assign survey:", error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const selectSurvey = (survey) => {
        setFormData(prev => ({
            ...prev,
            survey_id: survey.id,
            title: survey.title,
            description: survey.description || ""
        }));
        setErrors(prev => ({ ...prev, survey: null }));
        setActiveSection("settings");
    };

    const addTime = () => {
        if (formData.scheduled_times.length >= 24) return;
        setFormData(prev => ({
            ...prev,
            scheduled_times: [...prev.scheduled_times, "12:00"],
            frequency_type: prev.frequency_type === "once_per_day" && prev.scheduled_times.length >= 1 ? "multiple_times_per_day" : prev.frequency_type
        }));
    };

    const removeTime = (index) => {
        if (formData.scheduled_times.length <= 1) return;
        setFormData(prev => ({
            ...prev,
            scheduled_times: prev.scheduled_times.filter((_, i) => i !== index)
        }));
    };

    const updateTime = (index, value) => {
        setFormData(prev => ({
            ...prev,
            scheduled_times: prev.scheduled_times.map((t, i) => i === index ? value : t)
        }));
    };

    const toggleDay = (day) => {
        setFormData(prev => ({
            ...prev,
            days_of_week: prev.days_of_week.includes(day)
                ? prev.days_of_week.filter(d => d !== day)
                : [...prev.days_of_week, day].sort()
        }));
    };

    if (!isOpen) return null;

    return createPortal(
        <div className="drawer-root" role="presentation">
            <div className="drawer-backdrop" onClick={onClose} aria-hidden="true" />
            <div className="drawer" role="dialog" aria-modal="true" aria-labelledby="survey-assignment-title">
                <div className="drawer-header">
                    <div className="drawer-header-content">
                        <div>
                            <h2 id="survey-assignment-title">{isEditing ? "Редактировать назначение" : "Назначить опрос"}</h2>
                            {patient && <p className="drawer-subtitle">{patient.full_name || `ID: ${patient.id}`}</p>}
                        </div>
                            <button className="drawer-close" onClick={onClose} aria-label="Закрыть">
                            <ImportedXIcon size={20} />
                        </button>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="drawer-form">
                    <div className="section-nav">
                        {!isEditing && (
                            <button type="button" className={`section-nav-item ${activeSection === 'select' ? 'active' : ''}`} onClick={() => setActiveSection('select')}>
                                <ImportedFileTextIcon size={18} />
                                <span>Опрос</span>
                                {errors.survey && <span className="nav-error">!</span>}
                            </button>
                        )}
                        <button type="button" className={`section-nav-item ${activeSection === 'settings' ? 'active' : ''}`} onClick={() => setActiveSection('settings')}>
                            <StethoscopeIcon size={18} />
                            <span>Настройки</span>
                            {errors.title && <span className="nav-error">!</span>}
                        </button>
                        <button type="button" className={`section-nav-item ${activeSection === 'schedule' ? 'active' : ''}`} onClick={() => setActiveSection('schedule')}>
                            <ImportedRepeatIcon size={18} />
                            <span>Расписание</span>
                            {errors.days && <span className="nav-error">!</span>}
                        </button>
                    </div>

                    <div className="drawer-body">
                        {activeSection === "select" && !isEditing && (
                            <div className="drawer-section">
                                <div className="section-title">
                                    <ImportedFileTextIcon size={20} />
                                    <h3>Выберите опрос</h3>
                                </div>

                                {loading ? (
                                    <div className="drawer-loading">Загрузка опросов...</div>
                                ) : surveys.length === 0 ? (
                                    <div className="drawer-empty">
                                        <p>У вас пока нет созданных опросов</p>
                                        <button type="button" className="btn-secondary" onClick={() => navigate(ROUTES.SURVEYS)}>
                                            Создать опрос
                                        </button>
                                    </div>
                                ) : (
                                    <div className="survey-list">
                                        {surveys.map(survey => (
                                            <div
                                                key={survey.id}
                                                className={`survey-item ${formData.survey_id === survey.id ? 'selected' : ''}`}
                                                onClick={() => selectSurvey(survey)}
                                            >
                                                <div className="survey-icon">
                                                    <ImportedFileTextIcon size={18} />
                                                </div>
                                                <div className="survey-info">
                                                    <h4>{survey.title}</h4>
                                                    {survey.description && <p>{survey.description}</p>}
                                                </div>
                                                {formData.survey_id === survey.id && <ImportedCheckIcon className="survey-check" />}
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {errors.survey && <span className="field-error">{errors.survey}</span>}
                            </div>
                        )}

                        {activeSection === "settings" && (
                            <div className="drawer-section">
                                <div className="section-title">
                                    <StethoscopeIcon size={20} />
                                    <h3>Основная информация</h3>
                                </div>

                                {isEditing && (
                                    <div className="form-field">
                                        <label>Опрос</label>
                                        <div className="survey-readonly">
                                            <ImportedFileTextIcon size={18} />
                                            <span>{surveys.find(s => s.id === formData.survey_id)?.title || `ID: ${formData.survey_id}`}</span>
                                        </div>
                                    </div>
                                )}

                                <div className="form-field">
                                    <label>Название назначения *</label>
                                    <input
                                        type="text"
                                        value={formData.title}
                                        onChange={(e) => {
                                            setFormData(prev => ({ ...prev, title: e.target.value }));
                                            if (errors.title) setErrors(prev => ({ ...prev, title: null }));
                                        }}
                                        placeholder="Например: Утренний контроль"
                                        className={errors.title ? "error" : ""}
                                    />
                                    {errors.title && <span className="field-error">{errors.title}</span>}
                                </div>

                                <div className="form-field">
                                    <label>Описание</label>
                                    <textarea
                                        value={formData.description}
                                        onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                                        placeholder="Дополнительная информация для пациента..."
                                        rows={4}
                                    />
                                </div>

                                {isEditing && (
                                    <label className="checkbox-field">
                                        <input
                                            type="checkbox"
                                            checked={formData.is_active}
                                            onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                                        />
                                        <span>Назначение активно</span>
                                    </label>
                                )}
                            </div>
                        )}

                        {activeSection === "schedule" && (
                            <div className="drawer-section">
                                <div className="section-title">
                                    <ImportedRepeatIcon size={20} />
                                    <h3>Расписание</h3>
                                </div>

                                <div className="form-field">
                                    <label>Частота</label>
                                    <div className="frequency-options">
                                        {Object.entries(frequencyLabels).map(([value, label]) => (
                                            <button
                                                key={value}
                                                type="button"
                                                className={`frequency-option ${formData.frequency_type === value ? 'active' : ''}`}
                                                onClick={() => setFormData(prev => ({ ...prev, frequency_type: value }))}
                                            >
                                                {label}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {formData.frequency_type === "weekly" && (
                                    <div className="form-field">
                                        <label>Дни недели *</label>
                                        <div className={`weekdays-grid ${errors.days ? 'error' : ''}`}>
                                            {weekDays.map(day => (
                                                <button
                                                    key={day.value}
                                                    type="button"
                                                    className={`weekday-chip ${formData.days_of_week.includes(day.value) ? 'active' : ''}`}
                                                    onClick={() => toggleDay(day.value)}
                                                >
                                                    {day.label}
                                                </button>
                                            ))}
                                        </div>
                                        {errors.days && <span className="field-error">{errors.days}</span>}
                                    </div>
                                )}

                                {formData.frequency_type === "every_few_days" && (
                                    <div className="form-field">
                                        <label>Интервал</label>
                                        <div className="interval-control">
                                            <input
                                                type="number"
                                                min="2"
                                                max="30"
                                                value={formData.interval_days}
                                                onChange={(e) => setFormData(prev => ({ ...prev, interval_days: parseInt(e.target.value) || 2 }))}
                                            />
                                            <span>дней</span>
                                        </div>
                                    </div>
                                )}

                                <div className="form-field">
                                    <label>Время приёма</label>
                                    <div className="time-chips">
                                        {formData.scheduled_times.map((time, index) => (
                                            <div key={index} className="time-chip">
                                                <input
                                                    type="time"
                                                    value={time}
                                                    onChange={(e) => updateTime(index, e.target.value)}
                                                />
                                                {formData.scheduled_times.length > 1 && (
                                                    <button type="button" className="time-chip-remove" onClick={() => removeTime(index)}>
                                                        <ImportedXIcon size={14} />
                                                    </button>
                                                )}
                                            </div>
                                        ))}
                                        <button type="button" className="time-chip-add" onClick={addTime}>
                                            <ImportedPlusIcon />
                                        </button>
                                    </div>
                                </div>

                                <div className="form-field">
                                    <label>Дата начала</label>
                                    <input
                                        type="date"
                                        value={formData.start_date}
                                        onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                                        min={dayjs().format("YYYY-MM-DD")}
                                    />
                                </div>

                                <div className="form-field">
                                    <label>Дата окончания <span className="optional">(необязательно)</span></label>
                                    <input
                                        type="date"
                                        value={formData.end_date}
                                        onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                                        min={formData.start_date}
                                    />
                                    {formData.end_date && (
                                        <button type="button" className="clear-date" onClick={() => setFormData(prev => ({ ...prev, end_date: "" }))}>
                                            Очистить
                                        </button>
                                    )}
                                </div>

                                <div className="form-field">
                                    <label>Часовой пояс</label>
                                    <select
                                        value={formData.timezone}
                                        onChange={(e) => setFormData(prev => ({ ...prev, timezone: e.target.value }))}
                                    >
                                        {timezones.map(tz => <option key={tz.value} value={tz.value}>{tz.label}</option>)}
                                    </select>
                                </div>

                                <div className="form-field">
                                    <label>Напоминания</label>
                                    <div className="reminder-row">
                                        <span>Уведомить</span>
                                        <input
                                            type="number"
                                            min="0"
                                            max="10"
                                            value={formData.max_reminders}
                                            onChange={(e) => setFormData(prev => ({ ...prev, max_reminders: parseInt(e.target.value) || 0 }))}
                                        />
                                        <span>раз с интервалом</span>
                                        <input
                                            type="number"
                                            min="5"
                                            max="180"
                                            value={formData.reminder_interval_minutes}
                                            onChange={(e) => setFormData(prev => ({ ...prev, reminder_interval_minutes: parseInt(e.target.value) || 5 }))}
                                        />
                                        <span>мин</span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="drawer-footer">
                        <div className="drawer-footer-nav">
                            {activeSection !== (isEditing ? "settings" : "select") && (
                                <button type="button" className="btn-nav-prev" onClick={() => {
                                    const sections = isEditing ? ["settings", "schedule"] : ["select", "settings", "schedule"];
                                    const idx = sections.indexOf(activeSection);
                                    if (idx > 0) setActiveSection(sections[idx - 1]);
                                }}>
                                    <ChevronLeftIcon size={16} />
                                    Назад
                                </button>
                            )}
                            {activeSection !== "schedule" && (
                                <button type="button" className="btn-nav-next" onClick={() => {
                                    const sections = isEditing ? ["settings", "schedule"] : ["select", "settings", "schedule"];
                                    const idx = sections.indexOf(activeSection);
                                    if (idx < sections.length - 1) setActiveSection(sections[idx + 1]);
                                }}>
                                    Далее
                                    <ChevronRightIcon size={16} />
                                </button>
                            )}
                        </div>
                        <div className="drawer-footer-actions">
                            <button type="button" className="btn-secondary" onClick={onClose} disabled={isSubmitting}>
                                Отмена
                            </button>
                            <button type="submit" className="btn-primary" disabled={isSubmitting}>
                                {isSubmitting ? <span className="btn-label">Сохранение...</span> : (
                                    <span className="btn-label"><ImportedCheckIcon size={18} /> {isEditing ? "Сохранить" : "Назначить"}</span>
                                )}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>,
        document.body
    );
}

// Export wrapped with ErrorHandler
export default function PatientScheduledSurveysPage() {
    return (
        <>
            <SEO title="Запланированные опросы" description="Календарь и расписание предстоящих медицинских опросов" noindex={true} />
            <ErrorHandlerContextProvider>
                <PatientScheduled />
                <ErrorHandler />
            </ErrorHandlerContextProvider>
        </>
    );
}
